'use client';

import { motion } from 'framer-motion';
import ScreenContainer from './ScreenContainer';

const ScreenSkeleton = () => {
  // Shimmer animation that moves across the skeleton blocks
  const shimmerAnimation = {
    animate: {
      backgroundPosition: ['200% 0', '-200% 0'],
    },
    transition: {
      duration: 1.5,
      ease: 'linear',
      repeat: Infinity,
    },
  };

  // Stagger animation for skeleton blocks
  const containerVariants = {
    animate: {
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const itemVariants = {
    initial: {
      opacity: 0,
      scale: 0.95,
      y: 10,
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94], // easeOutQuad
      },
    },
  };

  // Skeleton block component with shimmer
  const SkeletonBlock = ({ className = '', delay = 0 }) => (
    <motion.div
      variants={itemVariants}
      className={`relative overflow-hidden bg-gray-200 rounded-lg ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <motion.div
        animate={shimmerAnimation.animate}
        transition={shimmerAnimation.transition}
        className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent"
        style={{
          backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
          backgroundSize: '200% 100%',
        }}
      />
    </motion.div>
  );

  return (
    <ScreenContainer>
      <motion.div
        variants={containerVariants}
        initial="initial"
        animate="animate"
        className="space-y-6"
      >
        {/* Title skeleton */}
        <div className="space-y-2">
          <SkeletonBlock className="h-8 sm:h-9 lg:h-10 w-3/4" />
          <SkeletonBlock className="h-4 sm:h-5 w-full" delay={50} />
        </div>

        {/* Input field skeleton */}
        <div className="space-y-2">
          <SkeletonBlock className="h-4 w-24" delay={100} />
          <SkeletonBlock className="h-11 sm:h-12 w-full" delay={150} />
        </div>

        {/* Radio/Options skeleton - simulating a grid layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          <div className="space-y-3">
            <SkeletonBlock className="h-4 w-20" delay={200} />
            <div className="space-y-2">
              <SkeletonBlock className="h-12 w-full" delay={250} />
              <SkeletonBlock className="h-12 w-full" delay={300} />
              <SkeletonBlock className="h-12 w-full" delay={350} />
            </div>
          </div>
          
          {/* Second column for desktop */}
          <div className="hidden lg:block space-y-3">
            <SkeletonBlock className="h-4 w-16" delay={400} />
            <SkeletonBlock className="h-12 w-48" delay={450} />
          </div>
        </div>

        {/* Card grid skeleton - for screens like genre selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <SkeletonBlock className="h-24 sm:h-28" delay={500} />
          <SkeletonBlock className="h-24 sm:h-28" delay={550} />
          <SkeletonBlock className="h-24 sm:h-28" delay={600} />
          <SkeletonBlock className="h-24 sm:h-28" delay={650} />
        </div>

        {/* Button skeleton - for navigation or actions */}
        <div className="flex justify-center mt-8">
          <SkeletonBlock className="h-12 w-32" delay={700} />
        </div>
      </motion.div>
    </ScreenContainer>
  );
};

export default ScreenSkeleton;